"use client";

import { useAuth } from "@/components/features/auth/hooks/use-auth";
import { useQuery } from "@tanstack/react-query";
import {
  getPlaceDetailsQueryOptions,
  getPlaceIdQueryOptions,
} from "../api/google-places.client";
import { getLocationPrefsQueryOptions } from "../api/location-prefs.client";
import {
  GetLocationPrefsParams,
  LocationPreferences,
} from "../api/location-prefs.shared";

// Extended type for backward compatibility including location name
interface LocationPreferencesWithName extends LocationPreferences {
  locationName?: string;
  locationNameWithState?: string;
  city?: string; // Frontend-computed field from Google Places API
  state?: string; // Frontend-computed field from Google Places API
}

export type UseLocationPrefsResult = {
  data: LocationPreferencesWithName | undefined;
  isLoading: boolean;
  error: Error | null;
};

/**
 * React Query hook for fetching location preferences with standard query behavior
 * Includes location name resolution for backward compatibility
 */
export function useLocationPrefs(
  params: GetLocationPrefsParams = {},
): UseLocationPrefsResult {
  const { user, loaded: isAuthLoaded } = useAuth();

  // Use userId from auth if not provided in params for backward compatibility
  // Convert null to undefined to match the query key generation logic
  const effectiveParams = {
    ...params,
    userId: params.userId ?? (user?.uid || undefined),
  };

  const queryResult = useQuery({
    ...getLocationPrefsQueryOptions(effectiveParams),
    // Gate user-dependent network request until auth is ready
    enabled: isAuthLoaded,
  });

  // Google Places lookups with React Query caching, using hooks at top-level
  const postalCode = queryResult.data?.postalCode ?? "";
  const placeIdResult = useQuery({
    ...getPlaceIdQueryOptions(postalCode),
    enabled: Boolean(postalCode),
  });
  const placeDetailsResult = useQuery({
    ...getPlaceDetailsQueryOptions(placeIdResult.data ?? ""),

    enabled: Boolean(placeIdResult.data),
  });

  // Return enhanced data with location name for backward compatibility
  return {
    ...queryResult,
    isLoading:
      queryResult.isLoading ||
      placeIdResult.isLoading ||
      placeDetailsResult.isLoading ||
      !isAuthLoaded,
    data:
      queryResult.data &&
      (queryResult.data.locationType === "beyond" ||
        queryResult.data.coordinates)
        ? ({
            ...queryResult.data,
            locationName: placeDetailsResult.data?.city,
            locationNameWithState: placeDetailsResult.data?.city
              ? `${placeDetailsResult.data?.city}, ${placeDetailsResult.data?.state}`
              : undefined,
            city: placeDetailsResult.data?.city,
            state: placeDetailsResult.data?.state,
          } as LocationPreferencesWithName)
        : {
            locationType: "beyond",
            radiusKm: 30,
            coordinates: undefined,
            postalCode: undefined,
          },
  };
}
